"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string };

type InfoCardBlok = SbBlokData & {
  title?: string;
  description?: string;
  link_label?: string;
  link?: StoryblokLink;
};

type InfoCardGridBlok = SbBlokData & {
  heading?: string;
  cards?: InfoCardBlok[];
  image?: Asset;
  bg_color?: unknown;
};

export default function InfoCardGrid({ blok }: { blok: InfoCardGridBlok }) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const { bg, hex, onDark } = resolveBg(blok.bg_color, "dusk-01");
  const cards = blok.cards || [];
  const imageSrc = blok.image?.filename;
  const imageAlt = blok.image?.alt || "";
  const hasImage = Boolean(imageSrc);
  // White cards would disappear on a white section, so give them a grey
  // border in that one case. Every other background reads fine on its own.
  const cardSurface =
    hex === "#ffffff" ? "bg-white border border-black/10" : "bg-white";

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,5rem)]">
        {blok.heading && (
          <h2
            className={`text-[clamp(2.25rem,4vw,3rem)] max-w-[820px] leading-[1.08] font-bold ${
              onDark ? "text-white" : "text-purple-05"
            }`}
          >
            {blok.heading}
          </h2>
        )}

        {(cards.length > 0 || hasImage) && (
          <div className="mt-8 flex flex-col gap-5 lg:mt-12 lg:flex-row lg:items-stretch lg:gap-6">
            {cards.length > 0 && (
              <div
                className={`grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:gap-6 ${
                  hasImage ? "lg:flex-1" : "lg:grid-cols-4"
                }`}
              >
                {cards.map((card) => {
                  const link = resolveLink(card.link, region);
                  const hasLink = Boolean(card.link_label && link.href !== "#");

                  const inner = (
                    <>
                      {card.title && (
                        <h3 className="text-h5 text-purple-05 leading-tight font-bold">
                          {card.title}
                        </h3>
                      )}
                      {card.description && (
                        <p className="text-body mt-3 leading-[1.56] text-[#595959]">
                          {card.description}
                        </p>
                      )}
                      {hasLink && (
                        <span className="text-body text-purple-03 mt-6 inline-flex w-fit items-center gap-[7px] font-extrabold group-hover:underline sm:mt-auto sm:pt-8">
                          {card.link_label}
                        </span>
                      )}
                    </>
                  );

                  const className = `group flex flex-col rounded-[20px] ${cardSurface} p-7 transition-colors hover:bg-purple-00 lg:p-8`;

                  return hasLink ? (
                    <a
                      key={card._uid}
                      {...storyblokEditable(card)}
                      href={link.href}
                      {...(link.target ? { target: link.target } : {})}
                      className={className}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div key={card._uid} {...storyblokEditable(card)} className={className}>
                      {inner}
                    </div>
                  );
                })}
              </div>
            )}

            {hasImage && imageSrc && (
              <div className="lg:w-[440px] lg:shrink-0">
                <div className="relative aspect-video overflow-hidden rounded-[20px] lg:aspect-auto lg:h-full lg:min-h-[420px]">
                  <Image
                    src={imageSrc}
                    alt={imageAlt}
                    fill
                    sizes="(min-width: 1115px) 440px, 100vw"
                    className="object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
